/**
 * L'association, nommée comme on la nomme à l'oral.
 *
 * Écrire « l'association X » partout paraît sûr, mais donne « l'association
 * Comité des Fêtes de la Vallée » — deux fois le mot qui désigne la chose.
 * Or la plupart des associations portent déjà leur nature dans leur nom :
 * comité, club, amicale, union, foyer.
 *
 * La règle tient en trois cas, dans cet ordre :
 *
 * — le nom commence par une voyelle, l'élision s'impose : « l'Amicale des
 *   pêcheurs », « l'Union sportive » ;
 * — le premier mot dit déjà la nature, on lui donne son article : « le Comité
 *   des Fêtes », « la Société de chasse » ;
 * — rien de tout cela, on introduit : « l'association Racing Club ».
 *
 * Ce n'est pas de la grammaire complète — aucune règle courte ne l'est en
 * français — mais elle couvre ce qu'on rencontre, et se trompe du côté poli.
 */

/** Les natures masculines : « le comité », « le club ». */
const MASCULINS = [
  'comite',
  'club',
  'cercle',
  'foyer',
  'groupe',
  'groupement',
  'syndicat',
  'collectif',
  'centre',
  'office',
  'stade',
  'racing',
];

/** Les natures féminines qui ne s'élident pas : « la société », « la ligue ». */
const FEMININS = [
  'societe',
  'ligue',
  'federation',
  'section',
  'compagnie',
  'troupe',
  'chorale',
  'confrerie',
  'maison',
  'jeunesse',
];

/** Les natures en h muet : « l’harmonie », et non « la harmonie ». */
const H_MUETS = ['harmonie', 'hospice'];

/** Sans accents ni ponctuation, pour comparer un mot à une liste. */
function simplifier(mot: string): string {
  return mot
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z]/g, '')
    .toLowerCase();
}

/**
 * Le nom précédé de son article, prêt à être inséré dans une phrase.
 *
 * @param nom      Le nom de l'association, tel qu'elle l'a saisi.
 * @param defaut   Ce qu'on écrit quand elle n'a pas encore de nom.
 */
export function avecArticle(nom?: string | null, defaut = 'notre association'): string {
  const propre = (nom ?? '').trim();
  if (propre === '') {
    return defaut;
  }

  const premier = simplifier(propre.split(/\s+/)[0] ?? '');

  // Voyelle : l'élision s'impose, quelle que soit la nature du mot. Le h en
  // est exclu — il est aspiré dans « le handball » et muet dans
  // « l'harmonie ». On ne devine pas : les rares cas muets sont nommés, les
  // autres tombent sur l'introduction neutre, qui ne choque jamais.
  if (/^[aeiouy]/.test(premier) || H_MUETS.includes(premier)) {
    return `l’${propre}`;
  }

  if (MASCULINS.includes(premier)) {
    return `le ${propre}`;
  }

  if (FEMININS.includes(premier)) {
    return `la ${propre}`;
  }

  return `l’association ${propre}`;
}
